import { ItemAdminType } from "../../types/types";

// The curation queue (EB-509).
//
// A sync publishes nothing: every product it creates lands `is_active: false`. So the result of a
// run is not ~1,800 products in the shop, it is ~1,800 products in a queue waiting for someone to
// complete them — and with the weekly schedule on, that queue grows every Sunday whether or not
// anyone is looking at it. These predicates are how that queue becomes visible.
//
// Kept out of the component so each rule can be asserted on plain data. The counts they produce are
// the metric that says whether the queue is shrinking, which is the whole point of the screen.

/** Rows the sync created carry this key prefix; curated rows keep their original slug id. */
export const SYNC_ID_PREFIX = "alg-";

/** Matches the weekly schedule, so "recently" means "since the last run" without hard-coding a date. */
export const RECENT_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

export type CurationFilter = "all" | "pending" | "inactive" | "recent";

/**
 * Inactive *and* created by the sync — the actual backlog.
 *
 * Identified by the `alg-` key rather than by `fuente`, deliberately. On prod 2,011 rows carry
 * `fuente: ["alegra"]` but only 1,837 were created by a sync; the other 174 are curated products
 * that a run merely linked by `referenceId`. Those are already published and are not queue work, so
 * counting them here would overstate the backlog by 174 and send someone hunting for products that
 * need nothing. The key is also what EB-499's backfill guards on, so the two agree.
 */
export const isPendingCuration = (item: ItemAdminType): boolean =>
  item.is_active !== true && (item.id?.startsWith(SYNC_ID_PREFIX) ?? false);

/**
 * Everything the storefront does not show, whatever its origin.
 *
 * Identical to `isPendingCuration` in today's data — every inactive row happens to be sync-created —
 * but they are different questions, and they diverge the moment an admin deactivates a curated
 * product. Collapsing them would hide exactly that case.
 */
export const isInactive = (item: ItemAdminType): boolean => item.is_active !== true;

/**
 * Touched within `RECENT_DAYS`. Answers "what did the last run change?".
 *
 * Rows with no `updated_at` are excluded rather than treated as ancient or recent — three prod rows
 * predate the field, and an unparseable date must not silently land in either bucket.
 */
export const isRecentlySynced = (item: ItemAdminType, now: number = Date.now()): boolean => {
  if (!item.updated_at) return false;

  const updated = Date.parse(item.updated_at);
  if (Number.isNaN(updated)) return false;

  return now - updated <= RECENT_DAYS * DAY_MS;
};

const PREDICATES: Record<CurationFilter, (item: ItemAdminType, now: number) => boolean> = {
  all: () => true,
  pending: (item) => isPendingCuration(item),
  inactive: (item) => isInactive(item),
  recent: (item, now) => isRecentlySynced(item, now),
};

/** Narrows the rows for one filter. Combines with the grid's quick filter, which runs on the result. */
export const applyCurationFilter = (
  items: ItemAdminType[],
  filter: CurationFilter,
  now: number = Date.now(),
): ItemAdminType[] =>
  filter === "all" ? items : items.filter((item) => PREDICATES[filter](item, now));

/**
 * One pass over the catalog for all four counts.
 *
 * Counts are always of the whole catalog, never of the current view — a chip that changed its own
 * number when you selected it could not answer "is the backlog going down?", which is the only
 * reason to show numbers at all.
 */
export const countByFilter = (
  items: ItemAdminType[] | undefined,
  now: number = Date.now(),
): Record<CurationFilter, number> => {
  const counts: Record<CurationFilter, number> = { all: 0, pending: 0, inactive: 0, recent: 0 };
  if (!items) return counts;

  for (const item of items) {
    counts.all += 1;
    if (isPendingCuration(item)) counts.pending += 1;
    if (isInactive(item)) counts.inactive += 1;
    if (isRecentlySynced(item, now)) counts.recent += 1;
  }

  return counts;
};
